import React, { useState, useEffect } from 'react';

const API = 'https://familybandhan-backend-933326390640.asia-south1.run.app';

const EVENT_TYPES = ['Birthday', 'Anniversary', 'Wedding', 'Reunion', 'Celebration', 'Other'];

function daysFromToday(dateStr) {
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(dateStr); d.setHours(0,0,0,0);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

function formatEventDate(dateStr) {
  const d = new Date(dateStr);
  return { day: d.getDate().toString().padStart(2,'0'), month: d.toLocaleString('default',{month:'short'}), year: d.getFullYear() };
}

export default function FamilyEvents({ familyEmail, onUpcomingCount }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', eventType: 'Birthday', customEventType: '', eventDate: '', description: '' });
  const [msg, setMsg] = useState('');

  useEffect(() => { loadEvents(); }, [familyEmail]);

  async function loadEvents() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/traditions/${encodeURIComponent(familyEmail)}/events`);
      const data = await r.json();
      const evts = data.events || [];
      setEvents(evts);
      const upcomingCount = evts.filter(e => { const d = daysFromToday(e.eventDate); return d >= 0 && d <= 30; }).length;
      if (onUpcomingCount) onUpcomingCount(upcomingCount);
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!form.name.trim()) { setMsg('Please enter an event name'); return; }
    if (!form.eventDate) { setMsg('Please select a date'); return; }
    if (form.eventType === 'Other' && !form.customEventType.trim()) { setMsg('Please describe the event type'); return; }
    setSaving(true); setMsg('');
    try {
      const r = await fetch(`${API}/api/traditions/${encodeURIComponent(familyEmail)}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error('Failed');
      setForm({ name: '', eventType: 'Birthday', customEventType: '', eventDate: '', description: '' });
      setShowForm(false);
      await loadEvents();
    } catch (e) {
      setMsg('Could not save — try again');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this event?')) return;
    try {
      await fetch(`${API}/api/traditions/${encodeURIComponent(familyEmail)}/events/${id}`, { method: 'DELETE' });
      const updated = events.filter(e => e.id !== id);
      setEvents(updated);
      const upcomingCount = updated.filter(e => { const d = daysFromToday(e.eventDate); return d >= 0 && d <= 30; }).length;
      if (onUpcomingCount) onUpcomingCount(upcomingCount);
    } catch (e) {}
  }

  const upcoming = events.filter(e => daysFromToday(e.eventDate) >= 0).sort((a,b) => new Date(a.eventDate)-new Date(b.eventDate));
  const past = events.filter(e => daysFromToday(e.eventDate) < 0).sort((a,b) => new Date(b.eventDate)-new Date(a.eventDate));
  const soonEvents = upcoming.filter(e => { const d = daysFromToday(e.eventDate); return d >= 0 && d <= 30; });

  if (loading) return <div style={s.loading}>Loading events…</div>;

  return (
    <div style={s.wrap}>

      {soonEvents.length > 0 && (
        <div style={s.notifBanner}>
          <div style={s.notifTitle}>🔔 {soonEvents.length} upcoming event{soonEvents.length > 1 ? 's' : ''} within 30 days</div>
          {soonEvents.map(e => {
            const d = daysFromToday(e.eventDate);
            const { day, month, year } = formatEventDate(e.eventDate);
            return (
              <div key={e.id} style={s.notifItem}>
                <span style={s.notifDays}>{d === 0 ? 'Today' : `In ${d} day${d>1?'s':''}`}</span>
                {e.name} — {day} {month} {year}
              </div>
            );
          })}
        </div>
      )}

      <div style={s.topBar}>
        <div style={s.pageTitle}>Family Events</div>
        <button style={s.addBtn} onClick={() => { setShowForm(true); setMsg(''); }}>+ Add Event</button>
      </div>

      {showForm && (
        <div style={s.formCard}>
          <div style={s.formTitle}>Add an event</div>

          <label style={s.label}>Event name *</label>
          <input style={s.input} value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Arjun's first birthday" maxLength={100} />

          <label style={s.label}>Event type</label>
          <select style={s.input} value={form.eventType} onChange={e => setForm(f=>({...f,eventType:e.target.value,customEventType:''}))}>
            {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          {form.eventType === 'Other' && (
            <>
              <label style={s.label}>Describe event type *</label>
              <input style={s.input} value={form.customEventType} onChange={e => setForm(f=>({...f,customEventType:e.target.value}))} placeholder="e.g. Graduation, Thread ceremony, Retirement" maxLength={50} />
            </>
          )}

          <label style={s.label}>Date *</label>
          <input type="date" style={s.input} value={form.eventDate} onChange={e => setForm(f=>({...f,eventDate:e.target.value}))} />

          <label style={s.label}>Description (optional)</label>
          <textarea style={{...s.input,resize:'vertical',minHeight:60}} value={form.description} onChange={e=>{if(e.target.value.length<=300)setForm(f=>({...f,description:e.target.value}))}} placeholder="Venue, notes, who attended…" />
          <div style={s.charCount}>{form.description.length} / 300</div>

          {msg && <div style={s.errMsg}>{msg}</div>}
          <div style={s.formBtns}>
            <button style={s.cancelBtn} onClick={() => { setShowForm(false); setMsg(''); }}>Cancel</button>
            <button style={s.saveBtn} onClick={handleAdd} disabled={saving}>{saving ? 'Saving…' : 'Save Event'}</button>
          </div>
        </div>
      )}

      {events.length === 0 && !showForm && (
        <div style={s.empty}>
          <div style={s.emptyIcon}>📅</div>
          <div style={s.emptyTitle}>No events recorded yet</div>
          <div style={s.emptyDesc}>Add birthdays, anniversaries, reunions — past and upcoming — to keep your family's timeline alive.</div>
          <button style={s.addBtn} onClick={() => setShowForm(true)}>+ Add first event</button>
        </div>
      )}

      {upcoming.length > 0 && (
        <>
          <div style={s.sectionLabel}>Upcoming</div>
          <div style={s.eventsList}>
            {upcoming.map(e => <EventRow key={e.id} event={e} onDelete={handleDelete} />)}
          </div>
        </>
      )}

      {past.length > 0 && (
        <>
          <div style={s.sectionLabel}>Past</div>
          <div style={s.eventsList}>
            {past.map(e => <EventRow key={e.id} event={e} onDelete={handleDelete} />)}
          </div>
        </>
      )}
    </div>
  );
}

function EventRow({ event, onDelete }) {
  const days = daysFromToday(event.eventDate);
  const { day, month, year } = formatEventDate(event.eventDate);
  const isPast = days < 0;
  const isSoon = days >= 0 && days <= 30;
  const displayType = event.eventType === 'Other' && event.customEventType ? event.customEventType : event.eventType;

  let badgeStyle = s.badgePast;
  let badgeText = String(year);
  if (!isPast) {
    badgeText = days === 0 ? 'Today!' : `In ${days} day${days>1?'s':''}`;
    badgeStyle = isSoon ? s.badgeSoon : s.badgeUpcoming;
  }

  return (
    <div style={s.eventRow}>
      <div style={s.dateBlock}>
        <div style={s.dateDay}>{day}</div>
        <div style={s.dateMonth}>{month}</div>
      </div>
      <div style={s.eventDivider} />
      <div style={s.eventInfo}>
        <div style={s.eventName}>{event.name}</div>
        <div style={s.eventType}>{displayType}</div>
        {event.description && <div style={s.eventDesc}>{event.description}</div>}
      </div>
      <span style={badgeStyle}>{badgeText}</span>
      <button style={s.deleteBtn} onClick={() => onDelete(event.id)} title="Delete">×</button>
    </div>
  );
}

const s = {
  wrap: { padding: '18px 20px', background: '#F7F4EF', minHeight: '100%' },
  loading: { padding: 60, textAlign: 'center', color: '#9CA3AF', fontSize: 14 },
  notifBanner: { background: '#fff', border: '1px solid #E5E7EB', borderLeft: '3px solid #E24B4A', borderRadius: 8, padding: '12px 16px', marginBottom: 16 },
  notifTitle: { fontSize: 12, fontWeight: 600, color: '#991B1B', marginBottom: 6 },
  notifItem: { fontSize: 12, color: '#374151', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8 },
  notifDays: { fontSize: 10, color: '#E24B4A', fontWeight: 600, background: '#FEE2E2', padding: '1px 7px', borderRadius: 8 },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  pageTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: '#1a0533', fontWeight: 600 },
  addBtn: { background: '#D4A017', color: '#1a0533', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  formCard: { background: '#fff', border: '1px solid #E8E0D0', borderRadius: 10, padding: '18px 20px', marginBottom: 20 },
  formTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 600, color: '#1a0533', marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5, marginTop: 12 },
  input: { width: '100%', border: '1px solid #E5E7EB', borderRadius: 7, padding: '8px 10px', fontSize: 13, color: '#111827', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' },
  charCount: { fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginTop: 3 },
  errMsg: { fontSize: 12, color: '#DC2626', marginTop: 8 },
  formBtns: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 },
  cancelBtn: { background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '8px 16px', fontSize: 13, color: '#6B7280', cursor: 'pointer' },
  saveBtn: { background: '#D4A017', color: '#1a0533', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '50px 24px', textAlign: 'center' },
  emptyIcon: { fontSize: 40 },
  emptyTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: '#374151', fontWeight: 600 },
  emptyDesc: { fontSize: 13, color: '#9CA3AF', lineHeight: 1.6, maxWidth: 300 },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 },
  eventsList: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 },
  eventRow: { background: '#fff', borderRadius: 8, border: '1px solid #E8E0D0', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, position: 'relative' },
  dateBlock: { minWidth: 40, textAlign: 'center', flexShrink: 0 },
  dateDay: { fontSize: 20, fontWeight: 700, color: '#1a0533', lineHeight: 1, fontFamily: "'Cormorant Garamond', serif" },
  dateMonth: { fontSize: 10, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' },
  eventDivider: { width: 1, height: 36, background: '#E5E7EB', flexShrink: 0 },
  eventInfo: { flex: 1, minWidth: 0 },
  eventName: { fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  eventType: { fontSize: 11, color: '#9CA3AF', marginTop: 2 },
  eventDesc: { fontSize: 11, color: '#6B7280', marginTop: 2, fontStyle: 'italic' },
  badgeUpcoming: { fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: '#FEF9C3', color: '#854D0E', border: '1px solid #D4A017', flexShrink: 0 },
  badgeSoon: { fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: '#FEE2E2', color: '#991B1B', flexShrink: 0 },
  badgePast: { fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: '#F3F4F6', color: '#6B7280', flexShrink: 0 },
  deleteBtn: { background: 'none', border: 'none', color: '#D1D5DB', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 0 0 6px', flexShrink: 0 },
};